import Model, {BudgetType, ModelSetting} from "../../../model/Model";
import Snapshot, {ISnapshotDetail} from "../../../model/Snapshot";
import IModelItem from "../../../model/IModelItem";
import Income, {IncomeType} from "../../../model/Income";
import {useEffect, useState} from "react";
import {useResources} from "../../../stores/ResourceProvider";
import {
  BudgetSection, discretionaryExpenseOptions, essentialExpenseOptions,
  foodAndPersonalCareExpenseOptions, givingAndMiscExpenseOptions,
  housingExpenseOptions,
  insuranceAndMedicalExpenseOptions,
  taxMenuOptions, travelAndEntertainmentExpenseOptions, vehiclesAndTransportationExpenseOptions
} from "./BudgetView";
import {Box, Chip, Grid, Tooltip, Typography} from "@mui/material";
import {createUUID, getISODateStartOfYear, getISODateToday} from "../../../stores/StoreUtilities";
import ModelStore from "../../../stores/ModelStore";
import * as React from "react";
import Schedule, {ScheduleNames} from "../../../model/Schedule";
import ChatbotOpenImg from "../../../images/ChatbotOpen.png";
import DialogButton from "../../../components/form/DialogButton";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IncomeEditDialog from "./IncomeEditDialog";
import Asset, {AssetCategory} from "../../../model/Asset";
import Deduction from "../../../model/Deduction";
import DeductionEditDialog from "./DeductionEditDialog";
import Expense from "../../../model/Expense";
import Tax from "../../../model/Tax";
import TaxEditDialog from "./TaxEditDialog";
import ExpenseEditDialog from "./ExpenseEditDialog";
import UserSetting, {IQuickStartSettings} from "../../../model/UserSetting";
import {
  BudgetIcon,
  DeductionExpenseIcon,
  getModelItemIcon,
  IncomeIcon,
  TaxExpenseIcon
} from "../../../styles/Icons";
import globalStyles, {globalColors} from "../../../styles/globalStyles";
import PaycheckEditDialog from "./PaycheckEditDialog";
import {NetWorthSection} from "../networth/NetWorthView";
import QuickStartWidget from "../dashboard/widgets/QuickStartWidget";

const BudgetQuickStart = ({
  step,
  model,
  snapshot,
  quickStartUserSetting,
  onSave,
  onNext,
  onExit
}: {
  step?: string
  model: Model
  snapshot?: Snapshot
  quickStartUserSetting?: UserSetting
  onSave(item: IModelItem): void
  onNext(settings: IQuickStartSettings): void
  onExit(settings: IQuickStartSettings): void
}) => {
  const [budgetType, setBudgetType] = useState<BudgetType>(BudgetType.Basic)
  const [income, setIncome] = useState<Income | undefined>()
  const [deduction, setDeduction] = useState<Deduction | undefined>()
  const [tax, setTax] = useState<Tax | undefined>()
  const [expense, setExpense] = useState<Expense | undefined>()

  const {modelStore} = useResources()

  const styles = {
    finn: {
      fontStyle:"italic"
    },
    chip: {
      mr: 1,
      my: 0.5,
      pl: 1,
      cursor: "pointer",
      borderRadius: "5px",
      boxShadow: "rgba(0, 0, 0, 0.15) 0px 3px 3px 0px"
    }
  }

  useEffect(() => {
    setBudgetType(model.getSetting(ModelSetting.BudgetType, BudgetType.Basic))
  },[step])

  const handleSave = (item: IModelItem, snapshotItem?: ISnapshotDetail) => {
    onSave(item)
    const typename = item.getTypename()
    if (typename === "Income") {
      setIncome(undefined)
    } else if (typename === "Deduction") {
      setDeduction(undefined)
    } else if (typename === "Tax") {
      setTax(undefined)
    } else if (typename === "Expense") {
      setExpense(undefined)
    }
  }

  const handlePrior = (event: any) => {
    let nextStep = ""
    let nextView = "budget"
    if (budgetType === BudgetType.Detailed) {
      switch (step) {
        case "Start":
          nextView = "networth"
          nextStep = NetWorthSection.Liabilities
          break
        case BudgetSection.Income:
          nextView = "networth"
          nextStep = NetWorthSection.Liabilities
          break
        case BudgetSection.Deductions:
          nextStep = BudgetSection.Income
          break
        case BudgetSection.Taxes:
          nextStep = BudgetSection.Deductions
          break
        case BudgetSection.Housing:
          nextStep = BudgetSection.Deductions
          break
        case BudgetSection.FoodAndPersonal:
          nextStep = BudgetSection.Housing
          break
        case BudgetSection.InsuranceAndMedical:
          nextStep = BudgetSection.FoodAndPersonal
          break
        case BudgetSection.VehiclesAndTransportation:
          nextStep = BudgetSection.InsuranceAndMedical
          break
        case BudgetSection.TravelAndEntertainment:
          nextStep = BudgetSection.VehiclesAndTransportation
          break
        case BudgetSection.GivingAndMisc:
          nextStep = BudgetSection.TravelAndEntertainment
          break
        case "Summary":
          nextStep = BudgetSection.GivingAndMisc
          break
      }
    } else {
      switch (step) {
        case "Start":
          nextView = "networth"
          nextStep = NetWorthSection.Liabilities
          break
        case BudgetSection.Income:
          nextView = "networth"
          nextStep = NetWorthSection.Liabilities
          break
        case BudgetSection.Deductions:
          nextStep = BudgetSection.Income
          break
        case BudgetSection.Taxes:
          nextStep = BudgetSection.Deductions
          break
        case BudgetSection.EssentialExpenses:
          nextStep = BudgetSection.Deductions
          break
        case BudgetSection.DiscretionaryExpenses:
          nextStep = BudgetSection.EssentialExpenses
          break
        case "Summary":
          nextStep = BudgetSection.DiscretionaryExpenses
          break
      }
    }
    if (nextStep) {
      onNext({status: "Active", view: nextView, step: nextStep})
    }
  }

  const handleNext = (event: any) => {
    let nextStep = ""
    let nextView = "budget"
    if (budgetType === BudgetType.Detailed) {
      switch (step) {
        case "Start":
          nextStep = BudgetSection.Income
          break
        case BudgetSection.Income:
          nextStep = BudgetSection.Deductions
          break
        case BudgetSection.Deductions:
          // nextStep = BudgetSection.Taxes
          nextStep = BudgetSection.Housing
          break
        case BudgetSection.Taxes:
          nextStep = BudgetSection.Housing
          break
        case BudgetSection.Housing:
          nextStep = BudgetSection.FoodAndPersonal
          break
        case BudgetSection.FoodAndPersonal:
          nextStep = BudgetSection.InsuranceAndMedical
          break
        case BudgetSection.InsuranceAndMedical:
          nextStep = BudgetSection.VehiclesAndTransportation
          break
        case BudgetSection.VehiclesAndTransportation:
          nextStep = BudgetSection.TravelAndEntertainment
          break
        case BudgetSection.TravelAndEntertainment:
          nextStep = BudgetSection.GivingAndMisc
          break
        case BudgetSection.GivingAndMisc:
          nextStep = "Summary"
          break
        case "Summary":
          onExit({status: "Completed", view: "budget", step: step ?? ""})
          break
      }
    } else {
      switch (step) {
        case "Start":
          nextStep = BudgetSection.Income
          break
        case BudgetSection.Income:
          nextStep = BudgetSection.Deductions
          break
        case BudgetSection.Deductions:
          // nextStep = BudgetSection.Taxes
          nextStep = BudgetSection.EssentialExpenses
          break
        case BudgetSection.Taxes:
          nextStep = BudgetSection.EssentialExpenses
          break
        case BudgetSection.EssentialExpenses:
          nextStep = BudgetSection.DiscretionaryExpenses
          break
        case BudgetSection.DiscretionaryExpenses:
          nextStep = "Summary"
          break
        case "Summary":
          onExit({status: "Completed", view: "budget", step: step ?? ""})
          break
      }
    }
    if (nextStep) {
      onNext({status: "Active", view: nextView, step: nextStep})
    }
  }

  const renderBudgetTypeChips = () => {
    const budgetTypeOptions = [BudgetType.Basic, BudgetType.Detailed]

    return (
      budgetTypeOptions.map((option: BudgetType) => {
        return (
          <Chip key={option}
                variant="outlined"
                icon={<BudgetIcon color={option === BudgetType.Basic ? globalColors.secondary : globalColors.primary}/>}
                label={`${option} Budget`}
                sx={styles.chip}
                // color={option === BudgetType.Basic ? "secondary" : "primary"}
                // style={{color: "white", fontWeight: "bold"}}
                clickable
                onClick={async() => {
                  setBudgetType(option)
                  model.setSetting(ModelSetting.BudgetType, option)
                  const update = await modelStore.saveSettings(model)
                  if (update) {
                    await onSave(update)
                  }
                  onNext({status: "Active", view: "budget", step: "Income"})
                }}
          />
        )
      })
    )
  }

  const renderIncomeChips = () => {
    const incomeOptions = [
      {label: "Paycheck (wages, salary or tips)", incomeType: IncomeType.WagesSalariesTips},
      {label: "Pensions & Annuities (taxable)", incomeType: IncomeType.PensionsAndAnnuitiesTaxable},
      {label: "Social Security Benefit", incomeType: IncomeType.SocialSecurityBenefits},
      {label: "Rental Income", incomeType: IncomeType.OtherIncome},
      {label: "Other Income", incomeType: IncomeType.OtherIncome}
    ]

    return (
      incomeOptions.map((item: {label: string, incomeType: IncomeType}, index) => {
        const {label, incomeType} = item
            const description = label.includes(" (") ? label.substring(0, label.indexOf(" (")) : label
            return (
              // <Tooltip title={<Typography variant="body2" sx={{lineHeight: 1.3}}>{typedef.label}</Typography>}  arrow placement="right-end" enterDelay={200}>
              <Chip key={`income-${index}`}
                    variant="outlined"
                    icon={<IncomeIcon/>}
                    label={label}
                    sx={styles.chip}
                    clickable
                    onClick={() => setIncome(new Income({
                      id: createUUID(),
                      accountId: model.accountId,
                      userId: model.userId,
                      modelId: model.id,
                      incomeType: incomeType,
                      description: description,
                      schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
                      start: getISODateStartOfYear(),
                      amount: 0,
                      amountDate: getISODateToday(),
                      archived: false,
                      sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length - 1].sortOrder + ModelStore.orderInterval : 0
                    }))}
              />
              // </Tooltip>
            )
        }
      )
    )
  }

  const getDeductionMenuOptions = () => {
    let options: string[] = []
    if (model) {
      const assets = model.getAssetsByCategory(AssetCategory.LiquidInvestableAssets)
        .filter((a: Asset) => {
          // Filter out assets that already have a deduction
          return (!a.archived && model.deductions.find((d: Deduction) => d.assetId === a.id) === undefined)
        })
      options = assets.map((a: Asset) => a.description)
    }
    return [...options, 'Other']
  }

  const renderDeductionChips = () => {
    const deductionOptions = getDeductionMenuOptions()

    return (
      deductionOptions.map((option: string) => {
        return (
          <Chip key={option}
                variant="outlined"
                icon={<DeductionExpenseIcon/>}
                label={option}
                sx={styles.chip}
                color="primary"
                clickable
                onClick={() => {
                  const item = new Deduction({
                    id: createUUID(),
                    accountId: model.accountId,
                    userId: model.userId,
                    modelId: model.id,
                    description: option,
                    schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
                    start: getISODateStartOfYear(),
                    amount: 0,
                    amountDate: getISODateToday(),
                    archived: false,
                    sortOrder: model.deductions.length > 0 ? model.deductions[model.deductions.length-1].sortOrder + ModelStore.orderInterval : 0
                  })
                  if (option !== 'Other' && model) {
                    const asset = model.getAssetByDescription(option)
                    if (asset) {
                      item.assetId = asset.id
                      item.asset = asset
                    }
                  }
                  setDeduction(item)
                }}
          />
        )
      })
    )
  }

  const renderTaxChips = () => {
    return (
      taxMenuOptions.map((option: Tax) => {
        return (
          <Chip key={option.taxType}
                variant="outlined"
                icon={<TaxExpenseIcon/>}
                label={option.description}
                sx={styles.chip}
                clickable
                onClick={() => setTax(new Tax({
                  id: createUUID(),
                  accountId: model.accountId,
                  userId: model.userId,
                  modelId: model.id,
                  description: option.description,
                  taxType: option.taxType,
                  schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
                  start: getISODateStartOfYear(),
                  amount: 0,
                  amountDate: getISODateToday(),
                  archived: false,
                  sortOrder: model.taxes.length > 0 ? model.taxes[model.taxes.length - 1].sortOrder + ModelStore.orderInterval : 0
                }))}
          />
        )
      })
    )
  }

  const renderExpenseChips = (options: Expense[]) => {
    const filteredOptions = options.filter((option: Expense) => model.expenses.find((e: Expense) => e.description === option.description) === undefined)

    return (
      filteredOptions.map((option: Expense) => {
        const icon = getModelItemIcon(option)
        return (
          <Tooltip title={<Typography variant="body2" sx={{lineHeight: 1.3}}>{option.tip ?? ""}</Typography>}  arrow placement="top-start" enterDelay={200}>
            <Chip key={option.description}
                  variant="outlined"
                  icon={icon}
                  label={option.description}
                  sx={styles.chip}
                  clickable
                  onClick={() => setExpense(new Expense({
                    id: createUUID(),
                    accountId: model.accountId,
                    userId: model.userId,
                    modelId: model.id,
                    description: option.description,
                    expenseCategory: option.expenseCategory,
                    discretionary: option.discretionary === true,
                    schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
                    start: getISODateStartOfYear(),
                    amount: 0,
                    amountDate: getISODateToday(),
                    archived: false,
                    sortOrder: model.expenses.length > 0 ? model.expenses[model.expenses.length - 1].sortOrder + ModelStore.orderInterval : 0
                  }))}
            />
          </Tooltip>
        )
      })
    )
  }

  if (step === "Start") {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Congratulations, you have completed entry of your assets and liabilities. Now let's work on your budget (income and spending).
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>Income</Typography>
            <Typography variant="body2">
              First, do you prefer a Basic budget with a few high-level expense categories or a Detailed budget with more specific expense categories?
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {renderBudgetTypeChips()}
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.Income) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Let's start with your sources of income. Please click on an income type or Other Income. When you're done, click Next Step.
            </Typography>
            <Typography variant="h5" color="primary">Income</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!income && renderIncomeChips()}
        </Grid>
        <Grid item xs={12} sm={income && income.incomeType === IncomeType.WagesSalariesTips ? 12 : 6} sx={{ml: "44px"}}>
          {income && income.incomeType === IncomeType.WagesSalariesTips &&
            <PaycheckEditDialog income={income} model={model} snapshot={snapshot} open quickForm
                             onSave={handleSave}
                             onClose={() => setIncome(undefined)}/>
          }
          {income && income.incomeType !== IncomeType.WagesSalariesTips &&
            <IncomeEditDialog income={income} model={model} snapshot={snapshot} open quickForm
                              onSave={handleSave}
                              onClose={() => setIncome(undefined)}/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!income &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.Deductions) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Are there any other recurring deductions or contributuions to asset accounts you would like to add?  We'll cover additional tax withholding in the next step.
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>Deductions & Contributions</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!income && renderDeductionChips()}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px"}}>
          {deduction &&
            <DeductionEditDialog deduction={deduction} model={model} snapshot={snapshot} open
                              onSave={handleSave}
                              onClose={() => setDeduction(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!deduction &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.Taxes) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Are there any other tax withholdings that you didn't add along with income sources. Select Next Step when you're done with this category.
            </Typography>
            <Typography variant="h5" color="primary">Tax Withholdings</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!tax && renderTaxChips()}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mt: 0}}>
          {tax &&
            <TaxEditDialog tax={tax} model={model} snapshot={snapshot} open
                                 onSave={handleSave}
                                 onClose={() => setTax(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!tax &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.EssentialExpenses) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant= "body2" sx={styles.finn} gutterBottom>
              Next add your essential (necessary) expenses. Select common expenses from the list below or select <em>All Essential</em> if you want to simply enter one amount to cover all your essential expenses.
            </Typography>
            <Typography variant="h5" color="primary">Essential Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(essentialExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                               onSave={handleSave}
                               onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.DiscretionaryExpenses) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your discretionary (optional) expenses. Select common expenses from the list below or select <em>All Discretionary</em> if you want to simply enter one amount to cover all your discretionary expenses.
            </Typography>
            <Typography variant="h5" color="primary">Discretionary Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(discretionaryExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                               onSave={handleSave}
                               onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.Housing) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your housing expenses. Select an expense from the list below or Next Step when you're done with this category.
            </Typography>
            <Typography variant="h5" color="primary">Housing Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(housingExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                           onSave={handleSave}
                           onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.FoodAndPersonal) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your food and personal expenses. Select an expense from the list below or Next Step when you're done with this category.
            </Typography>
            <Typography variant="h5" color="primary">Food & Personal Care Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(foodAndPersonalCareExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                               onSave={handleSave}
                               onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.InsuranceAndMedical) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your insurance and medical expenses. Select an expense from the list below or Next Step when you're done with this category.
            </Typography>
            <Typography variant="h5" color="primary">Insurance & Medical Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(insuranceAndMedicalExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                               onSave={handleSave}
                               onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.VehiclesAndTransportation) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your vehicles and transportation expenses. Select an expense from the list below or Next Step when you're done with this category.
            </Typography>
            <Typography variant="h5" color="primary">Vehicles & Transportation Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(vehiclesAndTransportationExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                               onSave={handleSave}
                               onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.TravelAndEntertainment) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your travel and entertainment expenses. Select an expense from the list below or Next Step when you're done with this category.
            </Typography>
            <Typography variant="h5" color="primary">Travel & Entertainment Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(travelAndEntertainmentExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                               onSave={handleSave}
                               onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === BudgetSection.GivingAndMisc) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your giving and miscellaneous expenses. Select an expense from the list below or Next Step when you're done with this category.
            </Typography>
            <Typography variant="h5" color="primary">Giving & Miscellaneous Expenses</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!expense && renderExpenseChips(givingAndMiscExpenseOptions)}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px", mb: 2}}>
          {expense &&
            <ExpenseEditDialog expense={expense} model={model} snapshot={snapshot} open
                               onSave={handleSave}
                               onClose={() => setExpense(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!expense &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === "Summary") {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Congratulations! You've completed your budget and reached the end of the Quick Start. <br/>Click Next Step to see your Dashboard and other views of your model.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box>
              <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                <ChevronLeftIcon/> Prev Step
              </DialogButton>
              <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                Next Step <ChevronRightIcon/>
              </DialogButton>
            </Box>
          </Box>
        </Grid>
      </Grid>
    )
  }

  return (
    <></>
  )
}

export default BudgetQuickStart